import React, { useState, useMemo } from 'react';
import { CreditCard, Lock, ShieldCheck } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { PAYABLE_SERVICES, toValidAmount, formatUSD } from '../lib/payment';

const Payment = () => {
  const [mode, setMode] = useState('service');
  const [serviceId, setServiceId] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [form, setForm] = useState({ name: '', email: '', note: '' });

  const selectedService = PAYABLE_SERVICES.find((s) => s.id === serviceId);

  const amount = useMemo(() => {
    if (mode === 'service') return selectedService ? selectedService.amount : 0;
    return toValidAmount(customAmount);
  }, [mode, selectedService, customAmount]);

  const amountDisplay = formatUSD(amount);

  const handleField = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  return (
    <section id="pay" className="bg-app py-24 border-t border-white/5">
      <div className="max-w-2xl mx-auto px-5 lg:px-8">
        <div className="text-center">
          <span className="text-xs tracking-[0.3em] text-sky-400 uppercase font-semibold">Payments</span>
          <h2 className="mt-3 font-cond font-700 text-4xl md:text-5xl text-white">Pay for Services</h2>
          <p className="mt-4 text-gray-400">
            Already got a quote? Pay securely by card. Choose a service or enter the amount we agreed on.
          </p>
        </div>

        <div className="mt-10 card-dark rounded-2xl p-6 md:p-8">
          <Tabs value={mode} onValueChange={setMode} className="w-full">
            <TabsList className="grid grid-cols-2 w-full bg-white/5 border border-white/10">
              <TabsTrigger value="service" className="data-[state=active]:bg-sky-500 data-[state=active]:text-[#04141f]">
                Pick a Service
              </TabsTrigger>
              <TabsTrigger value="custom" className="data-[state=active]:bg-sky-500 data-[state=active]:text-[#04141f]">
                Custom Amount
              </TabsTrigger>
            </TabsList>

            <TabsContent value="service" className="mt-5">
              <Label className="text-gray-300 text-sm">Service</Label>
              <Select value={serviceId} onValueChange={setServiceId}>
                <SelectTrigger className="mt-2 bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select a service to pay for" />
                </SelectTrigger>
                <SelectContent className="bg-[#0d1117] border-white/10 text-white max-h-72">
                  {PAYABLE_SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="focus:bg-sky-500/20">
                      {s.title} — {formatUSD(s.amount)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </TabsContent>

            <TabsContent value="custom" className="mt-5">
              <Label htmlFor="amount" className="text-gray-300 text-sm">Amount (USD)</Label>
              <div className="relative mt-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <Input
                  id="amount"
                  type="number"
                  min="1"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="pl-7 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-gray-300 text-sm">Your Name</Label>
              <Input
                id="name" name="name" value={form.name} onChange={handleField}
                placeholder="John Doe"
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-gray-300 text-sm">Email (for receipt)</Label>
              <Input
                id="email" name="email" type="email" value={form.email} onChange={handleField}
                placeholder="you@email.com"
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              />
            </div>
          </div>

          <div className="mt-4">
            <Label htmlFor="note" className="text-gray-300 text-sm">Invoice # / Note (optional)</Label>
            <Input
              id="note" name="note" value={form.note} onChange={handleField}
              placeholder="e.g. Invoice #1024 — Laptop screen repair"
              className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
          </div>

          <div className="mt-6 flex items-center justify-between rounded-lg bg-white/5 border border-white/10 px-4 py-3">
            <span className="text-sm text-gray-400">Total</span>
            <span className="font-cond font-700 text-2xl text-white">{amountDisplay}</span>
          </div>

          <Button
            type="button"
            disabled
            className="mt-5 w-full btn-blue h-12 text-base font-semibold opacity-70 cursor-not-allowed"
          >
            <CreditCard className="w-5 h-5 mr-2" /> Pay with Card
          </Button>

          <p className="mt-3 text-center text-xs text-sky-400/90">
            Secure card payments powered by Stripe — coming soon.
          </p>

          <div className="mt-5 flex items-center justify-center gap-5 text-gray-500 text-xs">
            <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Encrypted checkout</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Powered by Stripe</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Payment;
